import React, { createContext, ReactNode, useContext } from 'react';

import { Inquiry, InquiryStatus } from '../models/Inquiry';
import api from '../services/api';

interface GetInquiriesParams {
  search?: string;
  status?: InquiryStatus | null;
  sort?: 'newest' | 'oldest';
  page?: number;
  limit?: number;
}

interface InquiryContextType {
  createInquiry: (
    name: string,
    email: string,
    products: { productId: number; quantity: number }[],
    nip?: string,
    remarks?: string,
  ) => Promise<void>;
  getInquiries: (params: GetInquiriesParams) => Promise<{
    inquiries: Inquiry[];
    currentPage: number;
    totalPages: number;
    totalInquiries: number;
  }>;
  getInquiry: (inquiryId: number) => Promise<Inquiry>;
  changeInquiryStatus: (
    inquiryId: number,
    status: InquiryStatus,
  ) => Promise<void>;
}

const InquiryContext = createContext<InquiryContextType | undefined>(undefined);

export const useInquiry = () => {
  const context = useContext(InquiryContext);
  if (!context) {
    throw new Error('useInquiry must be used within a InquiryProvider');
  }
  return context;
};

interface InquiryProviderProps {
  children: ReactNode;
}

export const InquiryProvider: React.FC<InquiryProviderProps> = ({
  children,
}) => {
  const reactAppApiUrl = process.env.REACT_APP_API_URL;
  if (!reactAppApiUrl) {
    throw new Error('REACT_APP_API_URL is not set');
  }

  const apiUrl = new URL('inquiries/', reactAppApiUrl).toString();

  const createInquiry = async (
    name: string,
    email: string,
    products: { productId: number; quantity: number }[],
    nip?: string,
    remarks?: string,
  ) => {
    try {      
      await api.post(apiUrl, {
        name,
        email,
        products,
        nip,
        remarks,
      });
    } catch (error: any) {
      console.error('Failed to create Inquiry', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const getInquiries = async ({
    search,
    status,
    sort,
    page = 1,
    limit = 10,
  }: GetInquiriesParams): Promise<{
    inquiries: Inquiry[];
    currentPage: number;
    totalPages: number;
    totalInquiries: number;
  }> => {
    try {
      if (page < 1 || limit < 1) {
        throw new Error('Invalid pagination parameters');
      }

      // Używamy głównego endpointu do pobierania listy zapytań,
      // zamiast /search, który nie istnieje w API
      const response = await api.get(apiUrl, {
        params: {
          search,
          status,
          sort,
          page,
          limit,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch inquiries', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const getInquiry = async (inquiryId: number): Promise<Inquiry> => {
    try {
      // Upewniamy się, że inquiryId jest liczbą i dodajemy do URL jako segment ścieżki
      if (isNaN(Number(inquiryId))) {
        throw new Error('Invalid inquiry ID');
      }
      
      // Tworzymy poprawny URL dla pojedynczego zapytania
      const url = `${apiUrl}${inquiryId}`;
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch inquiry', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const changeInquiryStatus = async (
    inquiryId: number,
    status: InquiryStatus,
  ) => {
    try {
      // Aktualizacja aby pasowało do endpointu API: POST /status/:inquiryId
      await api.post(`${apiUrl}status/${inquiryId}`, {
        status,
      });
    } catch (error: any) {
      console.error('Failed to change inquiry status', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  return (
    <InquiryContext.Provider
      value={{
        createInquiry,
        getInquiries,
        getInquiry,
        changeInquiryStatus,
      }}
    >
      {children}
    </InquiryContext.Provider>
  );
};
